import React, { useState, useEffect } from 'react';
import { ArrowLeft, Package, Calendar, IndianRupee, MapPin, CheckCircle, Clock, Truck, X, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import './MyOrders.css';

const MyOrdersPage = ({ onBack }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${process.env.REACT_APP_BASE_URL}/payment/orders`);
      if (response.data.success) {
        setOrders(response.data.orders || []);
      } else {
        throw new Error(response.data.message || 'Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError(error.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle size={20} color="#10b981" />;
      case 'shipped':
      case 'out_for_delivery':
        return <Truck size={20} color="#3b82f6" />;
      case 'confirmed':
      case 'processing':
        return <Package size={20} color="#f59e0b" />;
      case 'pending':
        return <Clock size={20} color="#6b7280" />;
      default:
        return <Package size={20} color="#6b7280" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return '#10b981';
      case 'shipped':
      case 'out_for_delivery':
        return '#3b82f6';
      case 'confirmed':
      case 'processing':
        return '#f59e0b';
      case 'pending':
        return '#6b7280';
      default:
        return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
  };

  const closeOrderDetails = () => {
    setSelectedOrder(null);
  };

  // Helper function to get product image URL
  const getProductImage = (item) => {
    if (item.image) return item.image;
    if (item.productId?.image) return item.productId.image;
    if (item.productId?.images?.[0]) return item.productId.images[0];
    return null; // fallback to placeholder
  };

  if (loading) {
    return (
      <div className="my-orders-container">
        <div className="orders-header">
          <h1>My Orders</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-orders-container">
        <div className="orders-header">
          <h1>My Orders</h1>
        </div>
        <div className="error-container">
          <p>{error}</p>
          <button onClick={fetchOrders} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="my-orders-container">
      {/* Header */}
      <div className="orders-header">
        <h1>My Orders</h1>
        <div className="orders-count">
          {orders.length} {orders.length === 1 ? 'Order' : 'Orders'}
        </div>
      </div>

      {/* Orders List */}
      <div className="orders-list">
        {orders.length === 0 ? (
          <div className="empty-orders">
            <Package size={64} color="#9ca3af" />
            <h2>No Orders Yet</h2>
            <p>You haven't placed any orders yet.</p>
            <button
              className="shop-now-button"
              onClick={() => navigate('/')}
            >
              Start Shopping
            </button>
          </div>
        ) : (
          orders.map((order) => (

            <div
              key={order._id}
              className="order-card"
              onClick={() => handleOrderClick(order)}
            >
              <div className="order-header">
                <div className="order-info">
                  <h3 className="order-number">
                    Order #{order.orderNumber || order._id.toString().slice(-8).toUpperCase()}
                  </h3>
                  <p className="order-date">
                    <Calendar size={16} />
                    {formatDate(order.createdAt)}
                  </p>
                </div>
                <div className="order-status">
                  {getStatusIcon(order.status)}
                  <span style={{ color: getStatusColor(order.status) }}>
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1).replace('_', ' ')}
                  </span>
                </div>
              </div>
              <div className='orderHistory'>
                {/* Product Images Preview */}
                <div className="order-products-preview">

  {/* Always show only the first product's image */}
  {order.items?.length > 0 && (
    <div className="Oproduct-image-wrapper">
      <img
        src={getProductImage(order.items[0])}
        alt={order.items[0].name}
        className="product-thumbnail"
        onError={(e) => {
          e.target.src =
            'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect width="80" height="80" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="12"%3ENo Image%3C/text%3E%3C/svg%3E';
        }}
      />
    </div>
  )}

  {/* If more products exist, show a round "+X" badge */}
  {order.items?.length > 1 && (
    <div className="more-products-badge">
      +{order.items.length - 1}
    </div>
  )}
</div>


                <div className="order-details">
                  <div className="order-items">
                    {/* <strong>{order.items?.length || 0} Items</strong> */}
                    {order.items?.slice(0, 2).map((item, index) => (
                      <span key={index} className="item-name">
                        {item.name}
                        {item.quantity > 1 && ` (x${item.quantity})`}
                      </span>
                    ))}
                     {order.shippingAddress && (
                <div className="shipping-address">
                  <MapPin size={16} />
                  <span>
                    {order.shippingAddress.fullName} - {order.shippingAddress.address.slice(0, 30)}...
                  </span>
                </div>
              )}
                    {order.items?.length > 2 && (
                      <span className="more-items">
                        +{order.items.length - 2} more items
                      </span>
                    )}
                  </div>

                  <div className="order-amount">
                    <IndianRupee size={18} />
                    {order.totalAmount?.toLocaleString()}
                  </div>
                </div>
                <ChevronRight size={32} color='#28a745'/>
              </div>
             
            </div>
          ))
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="order-modal-overlay" onClick={closeOrderDetails}>
          <div className="order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                Order #{selectedOrder.orderNumber || selectedOrder._id.toString().slice(-8).toUpperCase()}
              </h2>
              <button className="close-button" onClick={closeOrderDetails}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-content">
              {/* Order Status */}
              <div className="modal-section">
                <h3>Order Status</h3>
                <div className="status-badge" style={{ backgroundColor: getStatusColor(selectedOrder.status) + '20', color: getStatusColor(selectedOrder.status) }}>
                  {getStatusIcon(selectedOrder.status)}
                  {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1).replace('_', ' ')}
                </div>
                <p className="order-date">
                  Placed on {formatDateTime(selectedOrder.createdAt)}
                </p>
                {selectedOrder.paidAt && (
                  <p className="order-date">
                    Paid on {formatDateTime(selectedOrder.paidAt)}
                  </p>
                )}
              </div>

              {/* Dispatch Information Section */}
              {selectedOrder.dispatchInfo && selectedOrder.dispatchInfo.partnerName && (
                <div className="modal-section dispatch-section">
                  <h3>🚚 Dispatch Information</h3>
                  <div className="dispatch-details">
                    <div className="dispatch-row">
                      <span className="label">Delivery Partner:</span>
                      <span className="value">{selectedOrder.dispatchInfo.partnerName}</span>
                    </div>
                    <div className="dispatch-row">
                      <span className="label">Contact Number:</span>
                      <span className="value">
                        <a href={`tel:${selectedOrder.dispatchInfo.contactNumber}`}>
                          {selectedOrder.dispatchInfo.contactNumber}
                        </a>
                      </span>
                    </div>
                    <div className="dispatch-row">
                      <span className="label">Dispatched On:</span>
                      <span className="value">{formatDateTime(selectedOrder.dispatchInfo.dispatchedAt)}</span>
                    </div>
                    {selectedOrder.dispatchInfo.notes && (
                      <div className="dispatch-row">
                        <span className="label">Notes:</span>
                        <span className="value">{selectedOrder.dispatchInfo.notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Items with Images */}
              <div className="modal-section">
                <h3>Items ({selectedOrder.items?.length || 0})</h3>
                <div className="items-list">
                  {selectedOrder.items?.map((item, index) => {
                    const imageUrl = getProductImage(item);
                    return (
                      <div key={index} className="modal-item">
                        <div className="modal-item-image">
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={item.name}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60"%3E%3Crect width="60" height="60" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="10"%3ENo Image%3C/text%3E%3C/svg%3E';
                              }}
                            />
                          ) : (
                            <div className="modal-item-placeholder">
                              <Package size={24} color="#9ca3af" />
                            </div>
                          )}
                        </div>
                        <div className="item-info">
                          <span className="item-name">{item.name}</span>
                          <span className="item-quantity">Qty: {item.quantity}</span>
                        </div>
                        <div className="item-price">
                          <IndianRupee size={16} />
                          {item.subtotal?.toLocaleString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Shipping Address */}
              {selectedOrder.shippingAddress && (
                <div className="modal-section">
                  <h3>Shipping Address</h3>
                  <div className="address-details">
                    <p><strong>{selectedOrder.shippingAddress.fullName}</strong></p>
                    <p>{selectedOrder.shippingAddress.address}</p>
                    <p>
                      {selectedOrder.shippingAddress.landmark && `${selectedOrder.shippingAddress.landmark}, `}
                      {selectedOrder.shippingAddress.state} - {selectedOrder.shippingAddress.pincode}
                    </p>
                    <p>Phone: {selectedOrder.shippingAddress.contactNumber}</p>
                    <p>Email: {selectedOrder.shippingAddress.email}</p>
                  </div>
                </div>
              )}

              {/* Order Summary */}
              <div className="modal-section">
                <h3>Order Summary</h3>
                <div className="order-summary">
                  <div className="summary-row">
                    <span>Items Total:</span>
                    <span>₹{selectedOrder.amount?.toLocaleString()}</span>
                  </div>
                  {selectedOrder.deliveryCharge > 0 && (
                    <div className="summary-row">
                      <span>Delivery Charge:</span>
                      <span>₹{selectedOrder.deliveryCharge?.toLocaleString()}</span>
                    </div>
                  )}
                  {selectedOrder.discount > 0 && (
                    <div className="summary-row">
                      <span>Discount:</span>
                      <span>-₹{selectedOrder.discount?.toLocaleString()}</span>
                    </div>
                  )}
                  {selectedOrder.taxAmount > 0 && (
                    <div className="summary-row">
                      <span>Tax:</span>
                      <span>₹{selectedOrder.taxAmount?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="summary-row total">
                    <span>Total Amount:</span>
                    <span>₹{selectedOrder.totalAmount?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrdersPage;